import { throttle } from 'lodash';
import { snapshot } from 'valtio';
import {
  navigateToApps,
  navigateToIssues,
} from '../../common/routing/app-navigator';
import { createSimpleAsyncAction } from 'async-utils';
import {
  FilterElements,
  FilterTypeSearch,
  FilterTypeToFilter,
} from '../../common/filters-utils/filters-utils';
import applicationsService from '../services';
import ApplicationsFiltersStore from '../stores/applications-filters-store';
import {
  clearFilters,
  setSearchValues,
  setStoreApplicationsFilters,
  setStoreApplicationsFiltersTypeSearch,
} from '../stores/applications-filters-store-actions';
import ApplicationsStore from '../stores/applications-store';
import {
  clearSelectedApplication,
  clearSelectedApps,
} from '../stores/applications-store-actions';
import { loadApplications } from './applications-actions';
import { FilterApplicationsType } from '../applications-types';
import { CategoryRiskNames } from 'common-types';

export const loadFilters = createSimpleAsyncAction(
  async (cache: boolean = true, limit: number = 100, offset?: number) => {
    const results = await applicationsService.getApplicationsFilters.execute(
      {
        limit: limit,
        offset: ApplicationsStore.offset,
        filters: ApplicationsFiltersStore.filterBy,
      },
      cache,
    );

    if (results) {
      setStoreApplicationsFilters(results);
      setStoreApplicationsFiltersTypeSearch(results);
    }
  },
  {
    asyncState: ApplicationsFiltersStore.loadingFilters,
    errorMessage: 'Failed to load applications filters',
  },
);

export const onChangeFilter = throttle(
  (e: React.ChangeEvent<HTMLInputElement>) => {
    const { loadingFilters } = snapshot(ApplicationsFiltersStore);
    const { loading } = snapshot(ApplicationsStore);
    if (loadingFilters.isPending() || loading.isPending()) {
      return;
    }
    clearSelectedApps();
    if (ApplicationsFiltersStore.filterBy[e.target.id]) {
      const index = ApplicationsFiltersStore.filterBy[e.target.id].indexOf(
        e.target.value,
      );
      if (index !== -1) {
        ApplicationsFiltersStore.filterBy[e.target.id].splice(index, 1);
      } else {
        ApplicationsFiltersStore.filterBy[e.target.id].push(e.target.value);
      }
    } else {
      ApplicationsFiltersStore.filterBy[e.target.id] = [e.target.value];
    }
    navigateToApps(ApplicationsFiltersStore.filterBy);
    if (ApplicationsStore.selectedApp) {
      selectedAppFilter();
    } else {
      loadApplications({ update: true });
    }
    loadFilters();
  },
  1000,
);

const selectedAppFilter = async () => {
  await loadApplications({ update: true });
  const selectedIssueAfterFilter = ApplicationsStore.applications?.find(
    app => app.appId === ApplicationsStore.selectedApp?.appId,
  );
  if (selectedIssueAfterFilter === undefined) {
    clearSelectedApplication();
  }
};

export const setInitialFilters = (encodedFilters: string | null) => {
  if (!encodedFilters) return;
  let filters = {};
  try {
    filters = JSON.parse(decodeURI(encodedFilters));
  } catch {}
  ApplicationsFiltersStore.filterBy = filters;
};

export const handleChangeFilterSlider = throttle(
  (range: number[] | number, filterName: FilterTypeToFilter) => {
    const { loadingFilters } = snapshot(ApplicationsFiltersStore);
    const { loading } = snapshot(ApplicationsStore);
    if (loadingFilters.isPending() || loading.isPending()) {
      return;
    }
    ApplicationsFiltersStore.filterBy[filterName] = {
      from: range[0],
      to: range[1],
    };

    const checkBusinessPriority =
      filterName === FilterTypeToFilter.BusinessPriority &&
      range[1] === ApplicationsFiltersStore.maxBuisnessPriority &&
      range[0] === 0;

    if (checkBusinessPriority) {
      clearFilters();
    }
    navigateToApps(ApplicationsFiltersStore.filterBy);
    loadApplications({ update: true });
    loadFilters();
  },
  1000,
);

export const setApplicationsFiltersTypeSearch = (
  applicationsFilters: FilterApplicationsType,
): void => {
  const { searchValues } = snapshot(ApplicationsFiltersStore);
  ApplicationsFiltersStore.appsFiltersTypeSearch = Object.keys(
    applicationsFilters,
  ).reduce((acc, key) => {
    acc[key] = searchValues[key]
      ? applicationsFilters[key].filter((f: FilterElements) =>
          f.label.toLocaleLowerCase().includes(searchValues[key].toLowerCase()),
        )
      : applicationsFilters[key];
    return acc;
  }, {} as FilterApplicationsType);
};

export const handleSearchFilterType = (
  searchInput: string,
  filterType: string,
) => {
  const { appsFiltersType } = snapshot(ApplicationsFiltersStore);
  if (appsFiltersType && filterType) {
    const newFilteredTypes = appsFiltersType[filterType].filter(
      (issueFilterType: FilterElements) =>
        issueFilterType.label
          .toLocaleLowerCase()
          .includes(searchInput.toLowerCase()),
    );
    setSearchValues(filterType, searchInput);
    setApplicationsFiltersTypeSearch({
      ...appsFiltersType,
      [FilterTypeSearch[filterType]]: newFilteredTypes,
    });
  }
};

export const onIssueChipClick = (event: React.MouseEvent<HTMLSpanElement>) => {
  const { selectedApp } = snapshot(ApplicationsStore);
  const { name } = event.currentTarget.dataset;
  if (name && selectedApp) {
    navigateToIssues({
      criticality: [CategoryRiskNames[name]],
      apps: [selectedApp.appName],
    });
  }
};
